import { ReactElement } from 'react';
import { Box, Button } from '@mui/material';
import {
  GridAddIcon,
  GridDeleteForeverIcon,
  GridPanelContent,
  GridPanelFooter,
  GridPanelWrapper,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { FiltersTranslationKeys } from 'intl/keys';
import type { IssueFilterPanelNode } from 'store/issue/models/filterModel/filterGroups';
import { FilterGroup } from './FilterGroup';
import { FilterLine, FilterTreeChildren } from './FilterLine';
import { FilterRowForm } from './FilterRowForm';
import { useFilterGroups } from './model';
import {
  filterFooterAddButtonsSx,
  filterFooterSx,
  filterGroupsPanelSx,
  filterPanelContentSx,
  type FilterTreeNodeKind,
} from './styles';
import type { FilterGroupsPanelProps } from './types';

type FilterTreePanelProps = FilterGroupsPanelProps & {
  /** Whether rows can be put into groups; off, the panel is one AND / OR over a flat list of rows. */
  allowGroups: boolean;
};

function nodeKindOf(node: IssueFilterPanelNode): FilterTreeNodeKind {
  if (node.kind === 'row') {
    return 'row';
  }
  return node.rows.length > 1 ? 'group' : 'singleRowGroup';
}

function FilterTreePanel({ filterFormProps, columnsSort, allowGroups }: FilterTreePanelProps): ReactElement {
  const intl = useIntl();
  const apiRef = useGridApiContext();
  const filterGroups = useFilterGroups();
  const removeAllLabel = String(apiRef.current.getLocaleText('filterPanelRemoveAll'));
  const addRowLabel = formatTranslation(intl, FiltersTranslationKeys.groupAddCondition);
  const addGroupLabel = formatTranslation(intl, FiltersTranslationKeys.groupAdd);
  const rowFormProps = {
    filterFormProps,
    columnsSort,
    onChange: filterGroups.changeRow,
    onRemove: filterGroups.removeRow,
  };

  return (
    <GridPanelWrapper>
      <GridPanelContent sx={filterPanelContentSx}>
        <Box sx={filterGroupsPanelSx}>
          <FilterTreeChildren
            logicOperator={filterGroups.logicOperator}
            onChangeLogicOperator={filterGroups.changeLogicOperator}
            items={filterGroups.nodes.map((node) => ({
              key: node.kind === 'row' ? node.row.id : node.groupId,
              kind: nodeKindOf(node),
              content:
                node.kind === 'row' ? (
                  <FilterLine onRemove={() => filterGroups.removeRow(node.row)}>
                    <FilterRowForm row={node.row} {...rowFormProps} />
                  </FilterLine>
                ) : (
                  <FilterGroup
                    group={node}
                    onChangeLogicOperator={filterGroups.changeGroupLogicOperator}
                    {...rowFormProps}
                  />
                ),
            }))}
          />
        </Box>
      </GridPanelContent>
      <GridPanelFooter sx={filterFooterSx}>
        <Box sx={filterFooterAddButtonsSx}>
          <Button startIcon={<GridAddIcon />} onClick={filterGroups.addRow}>
            {addRowLabel}
          </Button>
          {allowGroups && (
            <Button startIcon={<GridAddIcon />} onClick={filterGroups.addGroup}>
              {addGroupLabel}
            </Button>
          )}
        </Box>
        {filterGroups.hasRows && (
          <Button startIcon={<GridDeleteForeverIcon />} onClick={filterGroups.removeAll}>
            {removeAllLabel}
          </Button>
        )}
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}

/**
 * The issue grid's filter rows as a tree (PT-7187), as in Kendo's Filter: the
 * root group with its AND / OR holds rows and groups of rows, each group has
 * its own AND / OR.
 */
export function FilterGroupsPanel(props: FilterGroupsPanelProps): ReactElement {
  return <FilterTreePanel {...props} allowGroups />;
}

/** The same panel for the grids whose filtering has no groups: one AND / OR over the rows. */
export function FilterRowsPanel(props: FilterGroupsPanelProps): ReactElement {
  return <FilterTreePanel {...props} allowGroups={false} />;
}
